import React, { useCallback, useEffect, useState } from 'react';
import type { OfficeAgentCode, OfficeRuleDto } from '../../../../shared/types';
import type { Ctx } from '../ctx';
import { ask } from '../dialogs';
import { Icon } from '../icons';
import { ErrorBanner, Toggle, useNow } from '../ui';
import { timeOf } from './OfficePage';

/**
 * Regole: come vuoi che lavori la squadra, scritto a parole. Valgono subito.
 * In alto «Come scrivi» e «Firma» (imparati dalle mail inviate, si possono correggere),
 * poi le regole: quelle scritte da te e quelle nate dalle tue correzioni.
 */

const SCOPE: Record<OfficeAgentCode | 'tutti', string> = { tutti: 'Tutta la squadra', segreteria: 'Segreteria', tecnico: 'Tecnico', opportunita: 'Opportunità' };
const SOURCE: Record<OfficeRuleDto['source'], string> = { marco: 'Scritta da te', correzione: 'Da una tua correzione', imparato: 'Imparata dalle tue mail' };

function LearnedBox({ ctx, title, hint, rule, kind, onSaved }: { ctx: Ctx; title: string; hint: string; rule: OfficeRuleDto | undefined; kind: 'stile' | 'firma'; onSaved: () => void }) {
  const [text, setText] = useState(rule?.text ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => setText(rule?.text ?? ''), [rule?.text]);
  const changed = text !== (rule?.text ?? '');
  const save = async () => {
    setBusy(true);
    try {
      if (rule) await ctx.api.put(`/api/office/rules/${rule.id}`, { text });
      else await ctx.api.post('/api/office/rules', { agent: 'segreteria', kind, text });
      onSaved();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <section className="panel ru-box">
      <header className="panel-head">
        <div className="grow">
          <h2>{title}</h2>
          <div className="subtitle">{rule ? (rule.source === 'imparato' ? 'Imparato dalle tue mail inviate' : rule.source === 'marco' ? 'Scritto da te' : SOURCE[rule.source]) : hint}</div>
        </div>
      </header>
      <ErrorBanner message={error} onClose={() => setError(null)} />
      <textarea rows={kind === 'firma' ? 4 : 9} value={text} placeholder={hint} onChange={(e) => setText(e.target.value)} />
      <div className="ru-actions">
        <span className="muted small">{kind === 'stile' ? 'Se lo cambi tu, non si riscrive più da solo.' : 'Va in fondo a ogni risposta preparata.'}</span>
        <button type="button" className="btn small dark" disabled={!changed || busy || text.trim().length < 2} onClick={save}>
          Salva
        </button>
      </div>
    </section>
  );
}

function RuleRow({ ctx, r, now, onChanged }: { ctx: Ctx; r: OfficeRuleDto; now: number; onChanged: () => void }) {
  const [edit, setEdit] = useState(false);
  const [text, setText] = useState(r.text);
  const save = async () => {
    await ctx.api.put(`/api/office/rules/${r.id}`, { text });
    setEdit(false);
    onChanged();
  };
  const remove = async () => {
    if (!(await ask(`Eliminare questa regola? «${r.text.slice(0, 120)}»`))) return;
    await ctx.api.del(`/api/office/rules/${r.id}`);
    onChanged();
  };
  return (
    <div className={`ru-row${r.active ? '' : ' off'}`}>
      <Toggle
        checked={r.active}
        onChange={async (v) => {
          await ctx.api.put(`/api/office/rules/${r.id}`, { active: v });
          onChanged();
        }}
      />
      <div className="grow">
        {edit ? (
          <div className="ru-edit">
            <textarea rows={3} value={text} onChange={(e) => setText(e.target.value)} />
            <div className="ru-actions">
              <button type="button" className="btn small ghost" onClick={() => setEdit(false)}>
                Annulla
              </button>
              <button type="button" className="btn small dark" disabled={text.trim().length < 3} onClick={save}>
                Salva
              </button>
            </div>
          </div>
        ) : (
          <div className="ru-text">{r.text}</div>
        )}
        <div className="muted tiny">
          {SCOPE[r.agent]} · {SOURCE[r.source]} · {timeOf(r.updatedAt ?? r.createdAt, now)}
        </div>
      </div>
      {!edit && (
        <div className="ru-btns">
          <button type="button" className="btn icon small ghost" title="Modifica" onClick={() => setEdit(true)}>
            <Icon name="edit" size={15} />
          </button>
          <button type="button" className="btn icon small ghost" title="Elimina" onClick={remove}>
            <Icon name="trash" size={15} />
          </button>
        </div>
      )}
    </div>
  );
}

export function OfficeRulesPage({ ctx }: { ctx: Ctx }) {
  const [rules, setRules] = useState<OfficeRuleDto[] | null>(null);
  const [text, setText] = useState('');
  const [scope, setScope] = useState<OfficeAgentCode | 'tutti'>('tutti');
  const [error, setError] = useState<string | null>(null);
  const now = useNow(60_000);
  const load = useCallback(() => {
    ctx.api
      .get<OfficeRuleDto[]>('/api/office/rules')
      .then(setRules)
      .catch((e) => setError((e as Error).message));
  }, [ctx.api]);
  useEffect(load, [load]);

  const add = async () => {
    if (text.trim().length < 3) return;
    try {
      await ctx.api.post('/api/office/rules', { agent: scope, kind: 'regola', text: text.trim() });
      setText('');
      load();
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const style = rules?.find((r) => r.kind === 'stile');
  const sign = rules?.find((r) => r.kind === 'firma');
  const list = (rules ?? []).filter((r) => r.kind === 'regola');
  return (
    <div className="ru-page">
      <div className="of-head">
        <div>
          <h1>Regole della squadra</h1>
          <div className="muted">Scrivi a parole come devono lavorare: valgono da subito.</div>
        </div>
      </div>
      <ErrorBanner message={error} onClose={() => setError(null)} />
      <div className="ru-grid">
        <LearnedBox ctx={ctx} title="Come scrivi" hint="La Segreteria lo impara dalle mail che hai mandato. Puoi correggerlo tu." rule={style} kind="stile" onSaved={load} />
        <LearnedBox ctx={ctx} title="Firma" hint="Es. Marco Natale - Tecnoside S.R.L. - tel. …" rule={sign} kind="firma" onSaved={load} />
      </div>
      <section className="panel ru-list">
        <header className="panel-head">
          <div className="grow">
            <h2>Regole</h2>
            <div className="subtitle">Quelle scritte da te e quelle nate dalle tue correzioni in «Cosa hanno fatto»</div>
          </div>
        </header>
        <div className="ru-add">
          <textarea rows={2} placeholder="Es. «Le mail della banca mettile sempre in Amministrazione» oppure «Con i clienti nuovi dai sempre del Lei»" value={text} onChange={(e) => setText(e.target.value)} />
          <div className="ru-actions">
            <select value={scope} onChange={(e) => setScope(e.target.value as OfficeAgentCode | 'tutti')}>
              {(Object.keys(SCOPE) as (OfficeAgentCode | 'tutti')[]).map((k) => (
                <option key={k} value={k}>
                  {SCOPE[k]}
                </option>
              ))}
            </select>
            <button type="button" className="btn small dark" disabled={text.trim().length < 3} onClick={add}>
              <Icon name="plus" size={14} /> Aggiungi la regola
            </button>
          </div>
        </div>
        {rules && list.length === 0 && <div className="muted small ru-empty">Nessuna regola: le tue correzioni in «Cosa hanno fatto» compaiono qui.</div>}
        {list.map((r) => (
          <RuleRow key={r.id} ctx={ctx} r={r} now={now} onChanged={load} />
        ))}
      </section>
    </div>
  );
}
